// ════════════════════════════════════════════════════════════════════════════
// LpaCard — surface primitivo (flat / elevated / glass / accent)
// ════════════════════════════════════════════════════════════════════════════

import React from "react";

export type LpaCardVariant = "flat" | "elevated" | "glass" | "accent";
export type LpaCardPadding = "none" | "sm" | "md" | "lg";

export interface LpaCardProps {
  variant?: LpaCardVariant;
  padding?: LpaCardPadding;
  /** Adiciona role="region" + aria-labelledby (forneça o id do título dentro) */
  asRegion?: boolean;
  ariaLabelledBy?: string;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  testId?: string;
}

const PADDING_MAP: Record<LpaCardPadding, string | number> = {
  none: 0,
  sm: 12,
  md: 18,
  lg: 24,
};

function variantStyle(variant: LpaCardVariant): React.CSSProperties {
  switch (variant) {
    case "flat":
      return {
        background: "var(--lpa-card)",
        border: "1px solid var(--lpa-border)",
        boxShadow: "none",
      };
    case "elevated":
      return {
        background: "var(--lpa-card)",
        border: "1px solid var(--lpa-border)",
        boxShadow: "var(--lpa-shadow-md)",
      };
    case "glass":
      return {
        background: "var(--lpa-glass-bg)",
        border: "1px solid var(--lpa-glass-border)",
        backdropFilter: "var(--lpa-glass-blur)",
        WebkitBackdropFilter: "var(--lpa-glass-blur)",
        boxShadow: "var(--lpa-glass-shadow)",
      };
    case "accent":
      return {
        background: "var(--lpa-accent-soft)",
        border: "1px solid var(--lpa-accent)",
        boxShadow: "none",
      };
  }
}

export default function LpaCard({
  variant = "flat",
  padding = "md",
  asRegion = false,
  ariaLabelledBy,
  children,
  className,
  style,
  onClick,
  testId,
}: LpaCardProps): React.ReactElement {
  return (
    <div
      role={asRegion ? "region" : undefined}
      aria-labelledby={ariaLabelledBy}
      data-testid={testId}
      className={className}
      onClick={onClick}
      style={{
        borderRadius: "var(--lpa-radius-lg)",
        padding: PADDING_MAP[padding],
        cursor: onClick ? "pointer" : undefined,
        transition: "border-color var(--lpa-tr-fast), box-shadow var(--lpa-tr-fast)",
        ...variantStyle(variant),
        ...style,
      }}
    >
      {children}
    </div>
  );
}
